import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, Scan, Check, Package, Trash2, Clock, Truck, RefreshCw, Layers, List as ListIcon, X, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as validationService from '@/services/validationService';
import { useOperationDraft } from '@/hooks/useOperationDraft';
import { DraftsMenu } from '@/components/DraftsMenu';
import { ResumedDraftBanner } from '@/components/ResumedDraftBanner';

import { EwbDocPanel } from '@/components/ewayBill/EwbDocPanel';
// Helper to extract error message from API response (handles FastAPI validation errors)
const getErrorMessage = (error, fallback = 'An error occurred') => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
  }
  return detail || fallback;
};

/**
 * GenericInternalTransfer - Reusable Internal Transfer component for all sections
 * 
 * Implements the 7-step workflow:
 * 1. Source creates transfer (status: created)
 * 2. Destination requests consignment (status: requested) - handled in Inward Consignment
 * 3. Source sees shipment request
 * 4. Source confirms shipment (status: shipped)
 * 5. Destination sees shipped transfer
 * 6. Destination accepts (status: received)
 * 7. Bundle Receipt Entry
 * 
 * Props:
 * - sectionName: Display name (e.g., "Inhouse Section")
 * - sectionKey: Database key (e.g., "Inhouse")
 * - backRoute: Route to go back to
 */

// Scanned-bundle panel (same card style as FOB Internal Transfer): summary
// counts, design chips (click = filter), newest scan on top + highlighted,
// "List" / "By design" views.
const NO_DESIGN = 'Design N/A';

const BundleCard = ({ r, isNewest, onRemove }) => (
  <div
    className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${isNewest
      ? 'bg-emerald-50 border-emerald-300'
      : 'bg-[#F5F1E8] border-[#E5DFD0] hover:bg-[#EDE9E0]'}`}
    data-testid={`scanned-bundle-${r.code}`}
  >
    <div className="flex items-center gap-3 min-w-0">
      <span className="text-sm font-semibold text-gray-500 w-8 flex-shrink-0">#{r.no}</span>
      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
      <div className="min-w-0">
        <span className="font-mono font-semibold break-all">{r.code}</span>
        {isNewest && <span className="ml-2 align-middle text-[10px] font-semibold text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5">LAST SCAN</span>}
        <span className="text-xs text-gray-600 block break-words">
          {r.sort_no ? <span className="font-semibold text-[#2D5F4F]">Sort {r.sort_no} • </span> : null}
          {r.design === NO_DESIGN ? <span className="text-amber-700">{NO_DESIGN}</span> : r.design}
          {r.shade ? ` / ${r.shade}` : ''}
          {r.hasQty ? ` • ${r.qty} pcs` : ''}
        </span>
      </div>
    </div>
    <Button variant="ghost" size="sm" onClick={() => onRemove(r.code)}
      className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0" data-testid={`remove-bundle-${r.code}`}>
      <Trash2 className="w-4 h-4" />
    </Button>
  </div>
);

const ScannedBundlesPanel = ({ scannedBundles, bundleInfo, onRemove }) => {
  const [view, setView] = useState('list');
  const [designFilter, setDesignFilter] = useState('');

  const rows = useMemo(() => scannedBundles.map((code, i) => {
    const info = bundleInfo[code] || {};
    return { code, no: i + 1, design: info.design_name || NO_DESIGN, shade: info.shade || '', sort_no: info.sort_no || '',
      qty: Number(info.qty) || 0, hasQty: info.qty != null && info.qty !== '' };
  }), [scannedBundles, bundleInfo]);

  const designs = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      const d = m.get(r.design) || { design: r.design, bundles: 0, pcs: 0 };
      d.bundles += 1;
      d.pcs += r.qty;
      m.set(r.design, d);
    });
    return [...m.values()].sort((a, b) => b.bundles - a.bundles || a.design.localeCompare(b.design));
  }, [rows]);

  useEffect(() => {
    if (designFilter && !designs.some((d) => d.design === designFilter)) setDesignFilter('');
  }, [designs, designFilter]);

  const totalPcs = rows.reduce((sum, r) => sum + r.qty, 0);
  const newest = rows.length ? rows[rows.length - 1].code : null;
  const shown = rows.filter((r) => !designFilter || r.design === designFilter).slice().reverse();

  return (
    <Card data-testid="it-scanned-bundles">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2"><Package className="w-5 h-5" /> Scanned Bundles</span>
          <span className="text-sm font-normal text-gray-500">
            {rows.length} bundle{rows.length !== 1 ? 's' : ''} · {totalPcs.toLocaleString('en-IN')} pcs · {designs.length} design{designs.length !== 1 ? 's' : ''}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-start gap-1.5">
          <div className="flex flex-wrap gap-1.5 flex-1" data-testid="it-design-chips">
            {designs.map((d) => {
              const active = designFilter === d.design;
              return (
                <button key={d.design} type="button" onClick={() => setDesignFilter(active ? '' : d.design)}
                  className={`text-xs rounded-full border px-2.5 py-1 transition ${active
                    ? 'bg-[#2D5F4F] border-[#2D5F4F] text-white'
                    : d.design === NO_DESIGN ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-white border-[#E5DFD0] text-gray-700 hover:border-[#2D5F4F]'}`}>
                  {d.design} · <b>{d.bundles}</b> bdl{d.pcs ? ` · ${d.pcs} pcs` : ''}
                </button>
              );
            })}
            {designFilter && (
              <button type="button" onClick={() => setDesignFilter('')} className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-0.5 px-1">
                <X className="w-3 h-3" /> Show all
              </button>
            )}
          </div>
          <div className="flex rounded-md border border-[#E5DFD0] overflow-hidden flex-shrink-0">
            <button type="button" onClick={() => setView('list')} className={`px-2.5 py-1 text-xs flex items-center gap-1 ${view === 'list' ? 'bg-[#1A1F2E] text-white' : 'bg-white text-gray-600'}`} data-testid="it-view-list">
              <ListIcon className="w-3.5 h-3.5" /> List
            </button>
            <button type="button" onClick={() => setView('design')} className={`px-2.5 py-1 text-xs flex items-center gap-1 border-l border-[#E5DFD0] ${view === 'design' ? 'bg-[#1A1F2E] text-white' : 'bg-white text-gray-600'}`} data-testid="it-view-design">
              <Layers className="w-3.5 h-3.5" /> By design
            </button>
          </div>
        </div>

        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
          {view === 'list'
            ? shown.map((r) => <BundleCard key={r.code} r={r} isNewest={r.code === newest} onRemove={onRemove} />)
            : designs.filter((d) => !designFilter || d.design === designFilter).map((d) => (
              <div key={d.design} className="space-y-2">
                <div className="flex items-center justify-between px-1 pt-1 text-sm">
                  <span className="font-semibold text-[#1A1F2E]">{d.design}</span>
                  <span className="text-xs text-gray-500">{d.bundles} bundle{d.bundles !== 1 ? 's' : ''}{d.pcs ? ` · ${d.pcs} pcs` : ''}</span>
                </div>
                {shown.filter((r) => r.design === d.design).map((r) => <BundleCard key={r.code} r={r} isNewest={r.code === newest} onRemove={onRemove} />)}
              </div>
            ))}
        </div>

        {designFilter && (
          <div className="text-xs text-gray-500">Showing {shown.length} of {rows.length} bundles · {designFilter}</div>
        )}
      </CardContent>
    </Card>
  );
};

const GenericInternalTransfer = ({ sectionName, sectionKey, backRoute }) => {
  const navigate = useNavigate();
  
  // Staff verification
  const [staffId, setStaffId] = useState('');
  const [staffVerified, setStaffVerified] = useState(false);
  const [staffName, setStaffName] = useState('');
  
  // Transfer form
  const [sections, setSections] = useState([]);
  const [targetSection, setTargetSection] = useState('');
  const [isRework, setIsRework] = useState(false);
  const [reworkReason, setReworkReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [bundleBarcode, setBundleBarcode] = useState('');
  const [scannedBundles, setScannedBundles] = useState([]);
  const [bundleInfo, setBundleInfo] = useState({}); // barcode -> { design_name, shade, qty } from validate-bundle
  const [submitting, setSubmitting] = useState(false);
  
  // Outgoing: Shipment requests FROM this section (status: requested)
  const [pendingShipments, setPendingShipments] = useState([]);
  const [confirmingShipment, setConfirmingShipment] = useState(null);
  
  // Incoming: Shipped transfers TO this section (status: shipped)
  const [shippedTransfers, setShippedTransfers] = useState([]);
  const [acceptingTransfer, setAcceptingTransfer] = useState(null);
  
  // Rejected transfers that can be reshipped
  const [rejectedTransfers, setRejectedTransfers] = useState([]);
  const [reshipModal, setReshipModal] = useState({ open: false, transfer: null });
  const [reshipSection, setReshipSection] = useState('');
  const [reshipRemarks, setReshipRemarks] = useState('');
  const [reshipSubmitting, setReshipSubmitting] = useState(false);
  
  const [activeTab, setActiveTab] = useState('outgoing');
  const [loading, setLoading] = useState(false);
  const [resumedDraft, setResumedDraft] = useState(null);

  const barcodeRef = useRef(null);

  const {
    drafts,
    activeDraftId,
    saving,
    lastSavedAt,
    autoSave,
    clearDraft,
    loadDraft,
    removeDraft,
  } = useOperationDraft({
    moduleKey: `section:${sectionKey}`,
    operationType: 'internal_transfer',
  });

  useEffect(() => {
    fetchSections();
    fetchAllData();
  }, []);

  // Auto-save the in-progress transfer (every 25 scans + debounced idle).
  useEffect(() => {
    if (!staffVerified) return;
    autoSave({
      label: `${sectionName} → ${targetSection || 'destination'}`,
      bundleCount: scannedBundles.length,
      documentId: `${sectionName}->${targetSection || 'destination'}`,
      documentNo: targetSection || '',
      payload: {
        staffId,
        staffName,
        targetSection,
        isRework,
        reworkReason,
        remarks,
        scannedBundles,
        bundleInfo,
      },
    });
  }, [scannedBundles, bundleInfo, staffVerified, targetSection, isRework, reworkReason, remarks, staffId, staffName, autoSave, sectionName]);

  const handleResumeDraft = async (draftId) => {
    try {
      const doc = await loadDraft(draftId);
      const p = doc.payload || {};
      setStaffId(p.staffId || '');
      setStaffName(p.staffName || '');
      setStaffVerified(!!p.staffId);
      setTargetSection(p.targetSection || '');
      setIsRework(!!p.isRework);
      setReworkReason(p.reworkReason || '');
      setRemarks(p.remarks || '');
      setScannedBundles(p.scannedBundles || []);
      setBundleInfo(p.bundleInfo || {});
      setActiveTab('outgoing');
      const summary = (drafts || []).find((x) => x.draft_id === draftId) || null;
      setResumedDraft(summary);
      toast.success(summary?.last_updated_by_name ? `Resumed draft — last worked on by ${summary.last_updated_by_name}` : 'Draft restored');
      setTimeout(() => barcodeRef.current?.focus(), 100);
    } catch {
      toast.error('Failed to restore draft');
    }
  };

  const handlePickDestination = (value) => {
    setTargetSection(value);
    if (scannedBundles.length > 0) return;
    const existing = (drafts || []).find((d) => d.document_id === `${sectionName}->${value}`);
    if (existing) handleResumeDraft(existing.draft_id);
    else setResumedDraft(null);
  };

  const handleStartFresh = async () => {
    const id = resumedDraft?.draft_id;
    if (id) {
      const ok = await removeDraft(id);
      if (!ok) { toast.error('Could not discard the shared draft — please try again'); return; }
    }
    setResumedDraft(null);
    setScannedBundles([]);
    setBundleInfo({});
    setTargetSection('');
    setRemarks('');
    setIsRework(false);
    setReworkReason('');
  };

  const fetchSections = async () => {
    try {
      const response = await apiClient.get('/internal-transfer/sections', {
        params: { exclude_section: sectionKey }
      });
      setSections(response.data.sections || []);
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // 1. Get shipment requests FROM this section (status: requested)
      const shipmentRes = await apiClient.get('/internal-transfer/shipment-requests', {
        params: { from_section: sectionKey }
      });
      setPendingShipments(shipmentRes.data.transfers || []);
      
      // 2. Get shipped transfers TO this section (status: shipped)
      const shippedRes = await apiClient.get('/internal-transfer/shipped-transfers', {
        params: { section: sectionKey }
      });
      setShippedTransfers(shippedRes.data.transfers || []);
      
      // 3. Get rejected transfers FROM this section that can be reshipped
      const rejectedRes = await apiClient.get('/internal-transfer/rejected-transfers', {
        params: { from_section: sectionKey }
      });
      setRejectedTransfers(rejectedRes.data.transfers || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStaffVerify = async () => {
    if (!staffId.trim()) {
      toast.error('Please enter staff ID');
      return;
    }
    try {
      const result = await validationService.validateStaffId(staffId);
      if (result.valid) {
        setStaffVerified(true);
        setStaffName(result.staff?.staff_name || result.staff?.name || 'Staff');
        toast.success(`Welcome, ${result.staff?.staff_name || 'Staff'}`);
        setTimeout(() => barcodeRef.current?.focus(), 100);
      } else {
        toast.error(result.message || 'Invalid staff ID');
      }
    } catch (error) {
      toast.error('Failed to verify staff');
    }
  };

  const handleScanBundle = async () => {
    if (!bundleBarcode.trim()) return;
    
    if (scannedBundles.includes(bundleBarcode)) {
      toast.error('Bundle already scanned');
      setBundleBarcode('');
      return;
    }
    
    try {
      const response = await apiClient.post(`/internal-transfer/validate-bundle?bundle_code=${encodeURIComponent(bundleBarcode)}&from_section=${encodeURIComponent(sectionKey)}`);
      
      if (response.data.valid) {
        const d = response.data;
        setScannedBundles([...scannedBundles, bundleBarcode]);
        setBundleInfo((m) => ({ ...m, [bundleBarcode]: { design_name: d.design_name || '', shade: d.shade || '', qty: d.qty ?? null, sort_no: d.sort_no || '' } }));
        toast.success(`Bundle ${bundleBarcode} added${d.design_name ? ` — ${d.design_name}` : ''}`);
      } else {
        toast.error(response.data.message || 'Invalid bundle');
      }
    } catch (error) {
      // Handle FastAPI validation errors (detail can be array or string)
      toast.error(getErrorMessage(error, 'Failed to validate bundle'));
    }
    
    setBundleBarcode('');
    setTimeout(() => barcodeRef.current?.focus(), 50);
  };

  const handleRemoveBundle = (barcode) => {
    setScannedBundles(scannedBundles.filter(b => b !== barcode));
  };

  const handleSubmitTransfer = async () => {
    if (!targetSection || scannedBundles.length === 0) {
      toast.error('Please select destination and scan bundles');
      return;
    }
    
    setSubmitting(true);
    try {
      const isReworkSubmit = isRework && (targetSection || '').toLowerCase().includes('inhouse');
      if (isReworkSubmit && !reworkReason.trim()) {
        toast.error('Provide a rework reason');
        setSubmitting(false);
        return;
      }
      await apiClient.post('/internal-transfer/create', {
        staff_id: staffId,
        staff_name: staffName,
        from_section: sectionKey,  // Include source section
        to_section: targetSection,
        bundle_codes: scannedBundles,
        remarks: remarks,
        transfer_type: isReworkSubmit ? 'REWORK' : 'STANDARD',
        rework_reason: isReworkSubmit ? reworkReason.trim() : undefined,
      });

      toast.success(
        isReworkSubmit
          ? `Rework transfer created to ${targetSection}. On receipt, source bundles will be retired and USNs released to the Rework Pool.`
          : `Transfer created to ${targetSection}. Waiting for destination to request consignment.`
      );

      // Reset form
      setScannedBundles([]);
      setBundleInfo({});
      setTargetSection('');
      setRemarks('');
      setIsRework(false);
      setReworkReason('');
      clearDraft(); // transfer submitted — remove its draft
      setResumedDraft(null);
      fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create transfer'));
    } finally {
      setSubmitting(false);
    }
  };

  // Step 4: Confirm shipment (source ships goods)
  const handleConfirmShipment = async (transferId) => {
    setConfirmingShipment(transferId);
    try {
      await apiClient.post('/internal-transfer/confirm-shipment', {
        transfer_id: transferId
      });
      toast.success('Shipment confirmed successfully');
      fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to confirm shipment'));
    } finally {
      setConfirmingShipment(null);
    }
  };

  // Step 6: Accept shipped transfer
  const handleAcceptTransfer = async (transferId) => {
    setAcceptingTransfer(transferId);
    try {
      await apiClient.post('/internal-transfer/accept', {
        transfer_id: transferId
      });
      toast.success('Transfer received! Proceed to Bundle Receipt Entry.');
      fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to accept transfer'));
    } finally {
      setAcceptingTransfer(null);
    }
  };

  // Handle reship rejected transfer
  const handleOpenReshipModal = (transfer) => {
    setReshipModal({ open: true, transfer });
    setReshipSection('');
    setReshipRemarks('');
    setReshipSubmitting(false); // was left true after a successful reship → next reship silently blocked
  };

  const handleCloseReshipModal = () => {
    setReshipModal({ open: false, transfer: null });
    setReshipSection('');
    setReshipRemarks('');
    setReshipSubmitting(false);
  };

  const handleReshipTransfer = async () => {
    if (!reshipSection || reshipSubmitting) {
      if (!reshipSection) toast.error('Please select a new destination section');
      return;
    }
    
    setReshipSubmitting(true);
    try {
      const response = await apiClient.post('/internal-transfer/reship', {
        transfer_id: reshipModal.transfer.transfer_id,
        new_destination_section: reshipSection,
        remarks: reshipRemarks || undefined
      });
      toast.success(response.data?.message || `Transfer reshipped to ${reshipSection}`);
      // Close modal first, then refresh data
      handleCloseReshipModal();
      // Refresh data in background
      fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reship transfer'));
      setReshipSubmitting(false);
    }
  };

  const handleReset = () => {
    setStaffVerified(false);
    setStaffId('');
    setStaffName('');
    setScannedBundles([]);
    setBundleInfo({});
    setTargetSection('');
    setRemarks('');
    setResumedDraft(null);
  };

  // Badge counts
  const outgoingCount = pendingShipments.length;
  const incomingCount = shippedTransfers.length;
  const rejectedCount = rejectedTransfers.length;

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate(backRoute)} 
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Internal Transfer</h1>
              <p className="text-sm text-[#E8DCC8]">{sectionName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DraftsMenu
              drafts={drafts}
              onResume={handleResumeDraft}
              onDelete={removeDraft}
              activeDraftId={activeDraftId}
              saving={saving}
              lastSavedAt={lastSavedAt}
              variant="dark"
            />
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchAllData}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="outgoing" className="flex items-center gap-2" data-testid="outgoing-tab">
              <Send className="w-4 h-4" />
              Outgoing
              {outgoingCount > 0 && (
                <Badge className="bg-orange-500 text-white ml-1">{outgoingCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="incoming" className="flex items-center gap-2" data-testid="incoming-tab">
              <Truck className="w-4 h-4" />
              Incoming
              {incomingCount > 0 && (
                <Badge className="bg-blue-500 text-white ml-1">{incomingCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="rejected" className="flex items-center gap-2" data-testid="rejected-tab">
              <RefreshCw className="w-4 h-4" />
              Rejected
              {rejectedCount > 0 && (
                <Badge className="bg-red-500 text-white ml-1">{rejectedCount}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* OUTGOING TRANSFERS TAB */}
          <TabsContent value="outgoing">
            {resumedDraft && (
              <ResumedDraftBanner draft={resumedDraft} onStartFresh={handleStartFresh} className="mb-4" />
            )}
            {/* Pending Shipment Confirmations */}
            {pendingShipments.length > 0 && (
              <Card className="mb-6 border-orange-200 bg-orange-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <Clock className="w-5 h-5" />
                    Pending Shipment Confirmations ({pendingShipments.length})
                  </CardTitle>
                  <CardDescription>Transfers requested by destination - confirm to ship</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pendingShipments.map((shipment) => (
                      <div 
                        key={shipment.transfer_id} 
                        className="flex items-center justify-between p-4 bg-white rounded-lg border border-orange-200"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
                              {shipment.transfer_id}
                            </Badge>
                            <span className="text-sm text-gray-600">→</span>
                            <Badge className="bg-purple-100 text-purple-700 border-0">
                              {shipment.to_section}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {shipment.bundle_count || shipment.bundle_codes?.length || 0} bundles
                          </p>
                          <p className="text-xs text-gray-400">
                            Requested: {shipment.requested_at ? new Date(shipment.requested_at).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                        <Button
                          onClick={() => handleConfirmShipment(shipment.transfer_id)}
                          disabled={confirmingShipment === shipment.transfer_id}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`confirm-shipment-${shipment.transfer_id}`}
                        >
                          {confirmingShipment === shipment.transfer_id ? (
                            <>Confirming...</>
                          ) : (
                            <>
                              <Truck className="w-4 h-4 mr-2" />
                              Confirm Shipment
                            </>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Create New Transfer */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5 text-indigo-500" />
                  Create New Transfer
                </CardTitle>
                <CardDescription>Transfer bundles to another section</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Staff Verification */}
                {!staffVerified ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Staff ID</Label>
                      <div className="flex gap-2">
                        <Input
                          value={staffId}
                          onChange={(e) => setStaffId(e.target.value)}
                          placeholder="Enter staff ID"
                          onKeyDown={(e) => e.key === 'Enter' && handleStaffVerify()}
                          data-testid="staff-id-input"
                        />
                        <Button onClick={handleStaffVerify} data-testid="verify-staff-btn">
                          Verify
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Staff Info */}
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <span className="font-medium">{staffName}</span>
                        <span className="text-sm text-gray-500">({staffId})</span>
                      </div>
                      <Button variant="outline" size="sm" onClick={handleReset}>
                        Change
                      </Button>
                    </div>

                    {/* Target Section */}
                    <div className="space-y-2">
                      <Label>Destination Section</Label>
                      <Select value={targetSection} onValueChange={handlePickDestination}>
                        <SelectTrigger data-testid="target-section-select">
                          <SelectValue placeholder="Select destination" />
                        </SelectTrigger>
                        <SelectContent>
                          {sections.map((section) => (
                            <SelectItem key={section.section_id || section.section_name} value={section.section_name}>
                              {section.section_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Rework toggle (visible only when destination is Inhouse) */}
                    {(targetSection || '').toLowerCase().includes('inhouse') && (
                      <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm">
                        <label className="flex items-center gap-2 font-medium text-amber-900 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isRework}
                            onChange={(e) => setIsRework(e.target.checked)}
                            data-testid="rework-toggle"
                            className="h-4 w-4 accent-amber-600"
                          />
                          Send for Rework / Re-Assortment
                        </label>
                        {isRework && (
                          <>
                            <div className="mt-2 text-xs text-amber-800">
                              On receipt at Inhouse, these bundles will be <b>fully retired</b>
                              and their USNs released into the Rework Pool. Per-USN source
                              lineage is preserved on the new packed bundle.
                            </div>
                            <Input
                              value={reworkReason}
                              onChange={(e) => setReworkReason(e.target.value)}
                              placeholder="Reason for rework (e.g. wrong color sort)"
                              className="mt-2 bg-white"
                              data-testid="rework-reason-input"
                            />
                          </>
                        )}
                      </div>
                    )}

                    {/* Bundle Scanning */}
                    <div className="space-y-2">
                      <Label>Scan Bundle Barcode</Label>
                      <div className="flex gap-2">
                        <Input
                          ref={barcodeRef}
                          value={bundleBarcode}
                          onChange={(e) => setBundleBarcode(e.target.value)}
                          placeholder="Scan or enter barcode"
                          onKeyDown={(e) => e.key === 'Enter' && handleScanBundle()}
                          data-testid="bundle-barcode-input"
                        />
                        <Button onClick={handleScanBundle} variant="outline">
                          <Scan className="w-4 h-4 mr-2" />
                          Add
                        </Button>
                      </div>
                    </div>

                    {/* Scanned Bundles */}
                    {scannedBundles.length > 0 && (
                      <ScannedBundlesPanel scannedBundles={scannedBundles} bundleInfo={bundleInfo} onRemove={handleRemoveBundle} />
                    )}

                    {/* Remarks */}
                    <div className="space-y-2">
                      <Label>Remarks (Optional)</Label>
                      <Textarea
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="Add any notes..."
                        rows={2}
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      onClick={handleSubmitTransfer}
                      disabled={!targetSection || scannedBundles.length === 0 || submitting}
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                      data-testid="submit-transfer-btn"
                    >
                      {submitting ? 'Creating...' : `Create Transfer (${scannedBundles.length} bundles)`}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* INCOMING SHIPMENTS TAB */}
          <TabsContent value="incoming">
            {shippedTransfers.length === 0 ? (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center text-gray-400">
                    <Truck className="w-16 h-16 mx-auto mb-3 opacity-20" />
                    <p className="text-lg font-medium">No incoming shipments</p>
                    <p className="text-sm mt-1">Shipped transfers from other sections will appear here</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-700">
                    <Truck className="w-5 h-5" />
                    Incoming Shipments ({shippedTransfers.length})
                  </CardTitle>
                  <CardDescription>Shipped transfers waiting for you to receive and accept</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {shippedTransfers.map((transfer) => (
                      <div 
                        key={transfer.transfer_id} 
                        className="p-4 bg-blue-50 rounded-lg border border-blue-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                                {transfer.transfer_id}
                              </Badge>
                              <span className="text-sm text-gray-600">from</span>
                              <Badge className="bg-indigo-100 text-indigo-700 border-0">
                                {transfer.from_section}
                              </Badge>
                              <Badge className="bg-green-100 text-green-700 border-0">
                                <Truck className="w-3 h-3 mr-1" />
                                Shipped
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-700">
                              <strong>{transfer.bundle_count || transfer.bundle_codes?.length || 0}</strong> bundles
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Shipped by {transfer.shipped_by_name || 'Unknown'} on {transfer.shipped_at ? new Date(transfer.shipped_at).toLocaleString() : 'N/A'}
                            </p>
                          </div>
                          <Button
                            onClick={() => handleAcceptTransfer(transfer.transfer_id)}
                            disabled={acceptingTransfer === transfer.transfer_id}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid={`accept-transfer-${transfer.transfer_id}`}
                          >
                            {acceptingTransfer === transfer.transfer_id ? (
                              <>Accepting...</>
                            ) : (
                              <>
                                <Check className="w-4 h-4 mr-2" />
                                Accept & Receive
                              </>
                            )}
                          </Button>
                        </div>
                        
                        {/* Bundle list preview */}
                        {transfer.bundle_codes && transfer.bundle_codes.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-blue-200">
                            <p className="text-xs text-gray-500 mb-2">Bundle Codes:</p>
                            <div className="flex flex-wrap gap-1">
                              {transfer.bundle_codes.slice(0, 5).map((code) => (
                                <Badge key={code} variant="outline" className="text-xs bg-white">
                                  {code}
                                </Badge>
                              ))}
                              {transfer.bundle_codes.length > 5 && (
                                <Badge variant="outline" className="text-xs bg-gray-100">
                                  +{transfer.bundle_codes.length - 5} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* REJECTED TRANSFERS TAB */}
          <TabsContent value="rejected">
            {rejectedTransfers.length === 0 ? (
              <Card className="border-gray-200">
                <CardContent className="py-12 text-center text-gray-500">
                  <RefreshCw className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No rejected transfers to reship</p>
                  <p className="text-sm mt-2">Rejected transfers will appear here for reshipping to a different section</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <RefreshCw className="w-5 h-5" />
                    Rejected Transfers ({rejectedTransfers.length})
                  </CardTitle>
                  <CardDescription>These transfers were rejected by the destination section. You can reship them to a different section.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {rejectedTransfers.map((transfer) => (
                      <div 
                        key={transfer.transfer_id} 
                        className="p-4 bg-white rounded-lg border border-red-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300">
                                {transfer.transfer_id}
                              </Badge>
                              <span className="text-sm text-gray-600">→</span>
                              <Badge className="bg-gray-100 text-gray-600 border-0 line-through">
                                {transfer.to_section}
                              </Badge>
                              <Badge className="bg-red-100 text-red-700 border-0">
                                Rejected
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-700">
                              <strong>{transfer.bundle_count || transfer.bundle_codes?.length || 0}</strong> bundles
                            </p>
                            <p className="text-sm text-red-600 mt-1">
                              <strong>Reason:</strong> {transfer.rejection_reason || 'No reason provided'}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Rejected by {transfer.rejected_by_name || 'Unknown'} on {transfer.rejected_at ? new Date(transfer.rejected_at).toLocaleString() : 'N/A'}
                            </p>
                            {transfer.reship_count > 0 && (
                              <p className="text-xs text-orange-600 mt-1">
                                Previously reshipped {transfer.reship_count} time(s)
                              </p>
                            )}
                          </div>
                          <Button
                            onClick={() => handleOpenReshipModal(transfer)}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid={`reship-transfer-${transfer.transfer_id}`}
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Reship
                          </Button>
                        </div>
                        
                        {/* Bundle list preview */}
                        {transfer.bundle_codes && transfer.bundle_codes.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-red-200">
                            <p className="text-xs text-gray-500 mb-2">Bundle Codes:</p>
                            <div className="flex flex-wrap gap-1">
                              {transfer.bundle_codes.slice(0, 5).map((code) => (
                                <Badge key={code} variant="outline" className="text-xs bg-white">
                                  {code}
                                </Badge>
                              ))}
                              {transfer.bundle_codes.length > 5 && (
                                <Badge variant="outline" className="text-xs bg-gray-100">
                                  +{transfer.bundle_codes.length - 5} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Reship Modal */}
        {reshipModal.open && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-blue-600" />
                  Reship Transfer
                </CardTitle>
                <CardDescription>
                  Select a new destination section for transfer {reshipModal.transfer?.transfer_id}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm">
                    <strong>Bundles:</strong> {reshipModal.transfer?.bundle_codes?.length || 0}
                  </p>
                  <p className="text-sm text-red-600">
                    <strong>Previously rejected by:</strong> {reshipModal.transfer?.to_section}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>New Destination Section *</Label>
                  <Select value={reshipSection} onValueChange={setReshipSection}>
                    <SelectTrigger data-testid="reship-section-select">
                      <SelectValue placeholder="Select new section" />
                    </SelectTrigger>
                    <SelectContent>
                      {sections
                        .filter(s => s.section_name !== reshipModal.transfer?.to_section)
                        .map((section) => (
                          <SelectItem key={section.section_name || section.section_id} value={section.section_name}>
                            {section.section_name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Remarks (Optional)</Label>
                  <Textarea
                    value={reshipRemarks}
                    onChange={(e) => setReshipRemarks(e.target.value)}
                    placeholder="Add any notes about this reship..."
                    rows={2}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleCloseReshipModal}
                    className="flex-1"
                    disabled={reshipSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleReshipTransfer}
                    disabled={!reshipSection || reshipSubmitting}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    data-testid="confirm-reship-btn"
                  >
                    {reshipSubmitting ? 'Reshipping...' : 'Confirm Reship'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        <div className="mt-6"><EwbDocPanel docType="branch_transfer" section={sectionKey} title="Internal transfer e-way bills (goods leaving the site)" /></div>
      </main>
    </div>
  );
};

export default GenericInternalTransfer;
